import React, { useMemo } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import { IconButton, Link, Tooltip } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

import { CompanyDetail } from '../../interfaces/companyDetail.interface';
import { USER_TYPES } from '../../services/user';
import { COMPANY_TYPES } from '../../services/vendor';
import DownloadReportSelect from '../DownloadReport/DownloadReportSelect';

const LEGACY_ASSESSMENTS_URL = 'https://mpaa.app.box.com/folder/188662031553';

const PaperItem = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#F8F9FD',
  padding: '16px',
  boxShadow: 'none',
  border: 'none',
  height: '100%',
}));

function getQualifications(detail: CompanyDetail): string {
  const qualifications: string[] = [];
  if (detail.canAssessPrem) qualifications.push('Site');
  if (detail.canAssessCloud) qualifications.push('Cloud');
  if (detail.assessorProfile?.hasZeroTrustCertification) qualifications.push('Zero Trust');
  return qualifications.join(', ');
}

export interface CompanyProfileHeaderProps {
  userType?: string;
  userCanAdminCompany: boolean;
  actionsMenuOpen: boolean;
  detail: CompanyDetail | null;
  loading: boolean;
  onAddLogoClick: () => void;
  onActionsMenuClick: (event: React.MouseEvent<HTMLElement>) => void;
}

export default function CompanyProfileHeader(props: CompanyProfileHeaderProps) {
  const {
    userType,
    userCanAdminCompany,
    actionsMenuOpen,
    detail,
    loading,
    onAddLogoClick,
    onActionsMenuClick,
  } = props;
  const theme = useTheme();

  const canClickSubsidiariesLink = useMemo(
    () => userType === USER_TYPES.TPN_ADMIN || userType === USER_TYPES.CONTENT_OWNER,
    [userType],
  );

  const qualifications = useMemo(() => (detail ? getQualifications(detail) : ''), [detail]);

  if (loading || !detail) {
    return (
      <Paper elevation={0} sx={{ mb: 4, position: 'relative', backgroundColor: 'transparent', boxShadow: 'none' }}>
        <Grid container spacing={2} padding={2} pt={0} mt={3} sx={{ alignItems: 'stretch' }}>
          {[0, 1, 2].map((column) => (
            <Grid item xs={12} md={4} padding={2} key={column}>
              <Skeleton variant="rectangular" height={28} sx={{ mb: 1, borderRadius: 1 }} />
              <Skeleton variant="text" />
              <Skeleton variant="text" />
              <Skeleton variant="text" width="60%" />
            </Grid>
          ))}
        </Grid>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ mb: 4, position: 'relative', backgroundColor: 'transparent', boxShadow: 'none' }}>
      <Grid container spacing={2} padding={2} pt={0} mt={3} sx={{ alignItems: 'stretch' }}>
        {/* Logo + company name */}
        <Grid item xs={12} md={4} padding={2}>
          {detail.logo ? <img style={{ maxWidth: '100%', maxHeight: '150px' }} src={detail.logo} alt="logo" /> : <></>}
          <Typography variant="h6">
            {detail.name}{' '}
            {(!detail.logo && userCanAdminCompany) && (
              <span>&nbsp;<Button size='small' variant='outlined' onClick={onAddLogoClick}>add logo</Button></span>
            )}
          </Typography>
          {(!!detail.parentCompany && userType === USER_TYPES.TPN_ADMIN) && (
            <Typography
              variant="body1"
              color="text.secondary"
              component="p"
              sx={{ mt: 0.5, display: 'inline-flex', alignItems: 'baseline', gap: '0.35ch' }}
            >
              <span>A</span>
              {canClickSubsidiariesLink ? (
                <Link
                  href={`/registry/${detail.parentCompany.id}`}
                  underline="none"
                  color="inherit"
                  sx={{
                    fontWeight: 700,
                    maxWidth: '100%',
                    display: 'inline-block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    '&:focus-visible': { outline: '2px solid', outlineOffset: 2 },
                  }}
                  title={detail.parentCompany.name}
                >
                  {detail.parentCompany.name}
                </Link>
              ) : (
                <Box
                  component="span"
                  sx={{
                    fontWeight: 700,
                    maxWidth: '100%',
                    display: 'inline-block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    cursor: 'default',
                  }}
                  title={detail.parentCompany.name}
                >
                  {detail.parentCompany.name}
                </Box>
              )}
              <span>company</span>
            </Typography>
          )}
          <Typography variant="body2" sx={{ color: theme.palette.customThemeColors?.darkGrey, fontSize: 12 }}>
            {detail.akaNames && detail.akaNames.length > 0 && <span>AKA: {detail.akaNames.join(', ')}</span>}
            {detail.fkaNames && detail.fkaNames.length > 0 && <span><br/>FKA: {detail.fkaNames.join(', ')}</span>}
            {detail.dbaNames && detail.dbaNames.length > 0 && <span><br/>DBA: {detail.dbaNames.join(', ')}</span>}
          </Typography>
          {detail.type === COMPANY_TYPES.CONTENT_OWNER && <Link href={LEGACY_ASSESSMENTS_URL} target='_blank'>View Legacy Assessments</Link>}
          {detail.type === COMPANY_TYPES.CONTENT_OWNER && [USER_TYPES.CONTENT_OWNER, USER_TYPES.TPN_ADMIN].includes(userType || '') && <><br/><DownloadReportSelect /></>}
          {detail.type === COMPANY_TYPES.ASSESSOR && (
            <Box sx={{ display: 'flex', gap: 2, mt: 1, pt: 1, borderTop: '1px solid rgba(0,0,0,.2)' }}>
              <Typography>Level: {detail.assessorLevel?.descriptor}</Typography>
              {qualifications && <Typography>Qualifications: {qualifications}</Typography>}
            </Box>
          )}
        </Grid>

        {/* Address */}
        <Grid item xs={12} md={4}>
          <PaperItem>
            <Typography variant="subtitle2">Address:</Typography>
            {detail.contact && (
              <Stack direction='column' spacing={2}>
                <Typography>
                  {detail.contact.address}<br/>
                  {detail.contact?.address2 && <>{detail.contact.address2}<br/></>}
                  {detail.contact?.address3 && <>{detail.contact.address3}<br/></>}
                  {(() => {
                    const cityState = [detail.contact.city, detail.contact.state].filter(Boolean).join(', ');
                    const cityStateZip = [cityState, detail.contact.zipcode].filter(Boolean).join(' ');
                    return cityStateZip ? <>{cityStateZip}<br/></> : null;
                  })()}
                  {detail.contact.country}<br/><br />
                  {detail.contact.phoneNumber}<br />
                  {detail.domain && (
                    <Link
                      href={detail.domain.startsWith('http') ? detail.domain : `https://${detail.domain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                    >
                      {detail.domain}
                    </Link>
                  )}
                </Typography>
              </Stack>
            )}
          </PaperItem>
        </Grid>

        {/* Primary Contact */}
        <Grid item xs={12} md={4}>
          {detail.primaryContactUser && (
            <>
              <Typography variant="subtitle2">Primary Contact:</Typography>
              <Stack direction='column' spacing={2}>
                <Typography sx={{ textOverflow: 'ellipsis', overflow: 'hidden' }}>
                  {detail.primaryContactUser?.firstName} {detail.primaryContactUser?.lastName}<br/>
                  <Tooltip title={detail.primaryContactUser?.email}>
                    <Link href={`mailto:${detail.primaryContactUser?.email}`} sx={{ textOverflow: 'ellipsis', margin: 0, overflow: 'hidden', cursor: 'pointer' }}>{detail.primaryContactUser?.email}</Link>
                  </Tooltip><br />
                  {detail.primaryContactUser?.contact?.phoneNumber}
                </Typography>
              </Stack>
            </>
          )}
        </Grid>

        {/* Three-dot menu */}
        {userCanAdminCompany && (
          <Tooltip title="Company Actions">
            <IconButton
              onClick={onActionsMenuClick}
              size="small"
              sx={{ position: 'absolute', top: '5px', right: '5px' }}
              aria-controls={actionsMenuOpen ? 'company-actions-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={actionsMenuOpen ? 'true' : undefined}
            >
              <MoreHorizIcon />
            </IconButton>
          </Tooltip>
        )}
      </Grid>
    </Paper>
  );
}
